import { caseUtils } from '@codametrix/ui-common';
import { connect } from 'react-redux';
import type { CodingScreenState, WorkspaceMode } from '../../project_starburst';
import {
  codingStateFromCaseRun,
  gradingStateFromCodeBenchCase
} from './stateMappers/caseRunToCodingScreenState';
import { CodingScreen } from './coding-screen';

// The same production mapping getDetail's reducer uses (cases.reducer.ts:731):
// pick the primary CaseRun out of the full CMxAPI.Case.
const { getCaseRun } = caseUtils;

type CodingScreenOwnProps = {
  mode?: WorkspaceMode;
  match?: { params?: Record<string, string | undefined> };
};

/**
 * Manual last-value memo. connect re-runs mapState on every redux
 * dispatch; rebuilding the mapped `CodingScreenState` (and its nested
 * `PatientInfoData`) each tick would hand connect a fresh object ref every time,
 * defeating its shallow-equal and re-rendering every slot consumer plus
 * re-allocating the provider value. Keyed on (mode, sourceRef): while the source
 * redux object is referentially stable the identical mapped-state ref is returned,
 * so connect skips the re-render.
 */
let memoMode: WorkspaceMode | undefined;
let memoSource: unknown;
let memoState: CodingScreenState | undefined;

function mapSourceToCodingScreenState(
  mode: WorkspaceMode | undefined,
  source: unknown
): CodingScreenState | undefined {
  if (mode === memoMode && source === memoSource) {
    return memoState;
  }
  memoMode = mode;
  memoSource = source;

  if (source == null) {
    memoState = undefined;
  } else if (mode === 'grading') {
    // Grading source is `state.codeBench.activeItem` — the CQA case record the existing grade-bench
    // renders from (loadCQAItemFromURL populates it). Declared `CMxAPI.CaseRun`, but really a
    // snake_case CQA case, so `gradingStateFromCodeBenchCase` reads it (account # off the flat
    // `hospital_account_record`). Guard the empty {} initial shell → undefined (nothing until loaded).
    const activeItem = source as CMxAPI.CaseRun;
    memoState =
      Object.keys(activeItem).length > 0
        ? gradingStateFromCodeBenchCase(activeItem)
        : undefined;
  } else {
    // Coding source is the raw full CMxAPI.Case (cases.reducer.ts:729). Guard on a
    // non-empty caseRuns first: getCaseRun throws 'no case runs available' otherwise,
    // and mapState runs on every tick — including before the case has loaded.
    const caseDetail = source as CMxAPI.Case;
    memoState = caseDetail.caseRuns?.length
      ? codingStateFromCaseRun(getCaseRun(caseDetail))
      : undefined;
  }
  return memoState;
}

/**
 * Maps redux → the props `CodingScreen` forwards into the widened shell, for BOTH modes. Each mode's
 * fetch (dispatched by `CodingScreen` on mount) populates redux but returns no value used here, so the
 * shell state is read back from the store:
 *   coding  → `getDetail` → `state.cases.apiResponse.activeItem` (full CMxAPI.Case, cases.reducer.ts:729)
 *   grading → `loadCQAItemFromURL` → `state.codeBench.activeItem` (the CQA case record the existing
 *             grade-bench itself renders from). `match` (route params) flows through from ownProps via
 *             connect's default merge to `CodingScreen`, which owns the fetch.
 */
export function mapStateToCodingScreenProps(
  state: CMx.ApplicationState,
  ownProps: CodingScreenOwnProps
): { mode?: WorkspaceMode; state?: CodingScreenState } {
  const mode = ownProps.mode;
  const source =
    mode === 'grading'
      ? state.codeBench?.activeItem
      : state.cases?.apiResponse?.activeItem;
  return { mode, state: mapSourceToCodingScreenState(mode, source) };
}

export const ConnectedCodingScreen = connect(mapStateToCodingScreenProps)(
  CodingScreen
);
